import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import AdminService from '../services/AdminService';
import toast from 'react-hot-toast';
import soundEffects from '../utils/soundEffects';
import { createRipple } from '../utils/vfx';
import {
  FaClipboardCheck,
  FaEdit,
  FaSave,
  FaTimes,
  FaSearch,
  FaTrash,
  FaPlus,
  FaArrowUp,
  FaArrowDown,
  FaGripVertical
} from 'react-icons/fa';
import './AdminQuizzesView.css';

const AdminQuizzesView = ({ adminLevel, hasAdminLevel }) => {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [editingQuestions, setEditingQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    loadQuizzes();
  }, []);

  const loadQuizzes = async () => {
    try {
      setLoading(true);
      const data = await AdminService.getQuizzes();
      setQuizzes(data.quizzes || []);
    } catch (error) {
      toast.error(error.message || 'Failed to load quizzes');
    } finally {
      setLoading(false);
    }
  };

  const handleEditQuestions = async (quiz) => {
    soundEffects.playClick();
    setSelectedQuiz(quiz);
    // Deep clone questions to avoid mutating original
    const quizQuestions = Array.isArray(quiz.questions) ? quiz.questions : [];
    setQuestions(quizQuestions.map((q, idx) => {
      const question = {
        ...q,
        id: q.id || idx + 1
      };
      
      // Ensure referenceAnswer exists for short_answer questions
      if (q.type === 'short_answer') {
        question.referenceAnswer = q.referenceAnswer || q.correctAnswer || '';
      }
      
      // Ensure options array exists for multiple_choice and preserve values
      if (q.type === 'multiple_choice') {
        if (!Array.isArray(q.options)) {
          question.options = ['', ''];
        } else {
          // Ensure all options are strings
          question.options = q.options.map(opt => opt || '');
        }
      }
      
      return question;
    }));
    setEditingQuestions(true);
  };

  const handleCancelEdit = () => {
    soundEffects.playClick();
    setEditingQuestions(false);
    setSelectedQuiz(null);
    setQuestions([]);
  };

  const handleSaveQuestions = async () => {
    try {
      soundEffects.playClick();
      
      // Validate questions
      for (let i = 0; i < questions.length; i++) {
        const q = questions[i];
        if (!q.question || !q.question.trim()) {
          toast.error(`Question ${i + 1} cannot be empty`);
          return;
        }
        if (!q.type) {
          toast.error(`Question ${i + 1} must have a type`);
          return;
        }
        if (q.type === 'multiple_choice' && (!q.options || q.options.length < 2)) {
          toast.error(`Question ${i + 1} (multiple choice) must have at least 2 options`);
          return;
        }
        if (q.type === 'short_answer' && !q.referenceAnswer && !q.correctAnswer) {
          toast.error(`Question ${i + 1} (short answer) must have a reference answer`);
          return;
        }
      }

      // Ensure referenceAnswer is set for short_answer questions
      const questionsToSave = questions.map(q => {
        if (q.type === 'short_answer') {
          const questionData = {
            ...q,
            referenceAnswer: q.referenceAnswer || q.correctAnswer || ''
          };
          // Remove correctAnswer if referenceAnswer is set (to avoid confusion)
          if (questionData.referenceAnswer) {
            delete questionData.correctAnswer;
          }
          return questionData;
        }
        return q;
      });

      // Validate that we have questions to save
      if (!questionsToSave || questionsToSave.length === 0) {
        toast.error('Cannot save: No questions to save');
        return;
      }

      // Ensure all questions have required fields
      const validatedQuestions = questionsToSave.map((q, index) => {
        // Ensure question has required fields
        if (!q.question || !q.type) {
          throw new Error(`Question ${index + 1} is missing required fields (question text or type)`);
        }
        
        // Clean up the question object - remove any undefined values
        const cleanQuestion = { ...q };
        Object.keys(cleanQuestion).forEach(key => {
          if (cleanQuestion[key] === undefined) {
            delete cleanQuestion[key];
          }
        });
        
        return cleanQuestion;
      });

      // Log what we're sending for debugging
      console.log('[AdminQuizzesView] Saving quiz questions:', {
        quizId: selectedQuiz.id,
        questionsCount: validatedQuestions.length,
        questions: validatedQuestions,
        questionsStringified: JSON.stringify(validatedQuestions),
        requestPayload: { questions: validatedQuestions }
      });

      // Ensure we're passing the data correctly
      const updatePayload = { questions: validatedQuestions };
      console.log('[AdminQuizzesView] Update payload:', updatePayload);
      
      await AdminService.updateQuiz(selectedQuiz.id, updatePayload);
      toast.success('Quiz questions updated successfully');
      setEditingQuestions(false);
      setSelectedQuiz(null);
      setQuestions([]);
      loadQuizzes();
    } catch (error) {
      console.error('Error updating quiz:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to update quiz questions';
      toast.error(errorMessage);
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: questions.length > 0 ? Math.max(...questions.map(q => q.id || 0)) + 1 : 1,
      type: 'multiple_choice',
      question: '',
      options: ['', ''],
      correctAnswer: 0,
      explanation: ''
    };
    setQuestions([...questions, newQuestion]);
  };

  const handleDeleteQuestion = (index) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setQuestions(questions.filter((_, i) => i !== index));
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    
    // When changing type, reset relevant fields
    if (field === 'type') {
      if (value === 'multiple_choice') {
        updated[index] = {
          ...updated[index],
          options: updated[index].options || ['', ''],
          correctAnswer: updated[index].correctAnswer !== undefined ? updated[index].correctAnswer : 0,
          referenceAnswer: undefined
        };
      } else if (value === 'true_false') {
        updated[index] = {
          ...updated[index],
          options: undefined,
          correctAnswer: typeof updated[index].correctAnswer === 'boolean' ? updated[index].correctAnswer : true,
          referenceAnswer: undefined
        };
      } else if (value === 'short_answer') {
        updated[index] = {
          ...updated[index],
          options: undefined,
          correctAnswer: undefined,
          referenceAnswer: updated[index].referenceAnswer || updated[index].correctAnswer || ''
        };
      }
    }
    
    // When changing correctAnswer for multiple_choice, ensure it's a number
    if (field === 'correctAnswer' && updated[index].type === 'multiple_choice') {
      updated[index].correctAnswer = parseInt(value) || 0;
    }
    
    setQuestions(updated);
  };

  const handleOptionChange = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const handleAddOption = (questionIndex) => {
    const updated = [...questions];
    if (!updated[questionIndex].options) {
      updated[questionIndex].options = [];
    }
    updated[questionIndex].options.push('');
    setQuestions(updated);
  };

  const handleRemoveOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    if (updated[questionIndex].options.length > 2) {
      updated[questionIndex].options.splice(optionIndex, 1);
      // Adjust correctAnswer if needed
      if (updated[questionIndex].correctAnswer >= optionIndex) {
        updated[questionIndex].correctAnswer = Math.max(0, updated[questionIndex].correctAnswer - 1);
      }
      setQuestions(updated);
    } else {
      toast.error('Multiple choice questions must have at least 2 options');
    }
  };

  const handleMoveQuestion = (index, direction) => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === questions.length - 1)) {
      return;
    }
    const updated = [...questions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setQuestions(updated);
  };

  const filteredQuizzes = quizzes.filter(quiz => {
    const matchesSearch = !searchTerm || 
      quiz.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quiz.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quiz.lesson_title?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  if (loading) {
    return <div className="loading">Loading quizzes...</div>;
  }

  if (editingQuestions) {
    return (
      <div className="admin-quizzes-view">
        <div className="quiz-editor-header">
          <h2>
            <FaClipboardCheck /> Edit Questions: {selectedQuiz?.title}
          </h2>
          <div className="editor-actions">
            <button className="btn-save" onClick={handleSaveQuestions}>
              <FaSave /> Save Questions
            </button>
            <button className="btn-cancel" onClick={handleCancelEdit}>
              <FaTimes /> Cancel
            </button>
          </div>
        </div>

        <div className="questions-editor">
          {questions.length === 0 ? (
            <div className="no-questions">
              <p>No questions yet. Click "Add Question" to get started.</p>
            </div>
          ) : (
            questions.map((question, index) => (
              <div key={question.id || index} className="question-editor-card">
                <div className="question-header">
                  <div className="question-number">
                    <FaGripVertical className="grip-icon" />
                    Question {index + 1}
                  </div>
                  <div className="question-controls">
                    <button
                      className="btn-move"
                      onClick={() => handleMoveQuestion(index, 'up')}
                      disabled={index === 0}
                      title="Move Up"
                    >
                      <FaArrowUp />
                    </button>
                    <button
                      className="btn-move"
                      onClick={() => handleMoveQuestion(index, 'down')}
                      disabled={index === questions.length - 1}
                      title="Move Down"
                    >
                      <FaArrowDown />
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteQuestion(index)}
                      title="Delete Question"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>

                <div className="question-body">
                  <div className="form-group">
                    <label>Question Type</label>
                    <select
                      value={question.type || 'multiple_choice'}
                      onChange={(e) => handleQuestionChange(index, 'type', e.target.value)}
                    >
                      <option value="multiple_choice">Multiple Choice</option>
                      <option value="true_false">True/False</option>
                      <option value="short_answer">Short Answer</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Question Text</label>
                    <textarea
                      value={question.question || ''}
                      onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                      rows="3"
                      placeholder="Enter the question text..."
                    />
                  </div>

                  {question.type === 'multiple_choice' && (
                    <>
                      <div className="form-group">
                        <label>Options</label>
                        
                        {/* Question preview */}
                        {question.question && question.question.trim() && (
                          <div className="question-preview-integrated">
                            <div className="preview-question">
                              <strong>Question:</strong> {question.question}
                            </div>
                          </div>
                        )}
                        
                        {question.options?.map((option, optIndex) => (
                          <div key={optIndex} className={`option-row-combined ${question.correctAnswer === optIndex ? 'correct-option' : ''}`}>
                            <span className="option-letter">
                              {String.fromCharCode(65 + optIndex)}.
                            </span>
                            <input
                              type="text"
                              value={option || ''}
                              onChange={(e) => handleOptionChange(index, optIndex, e.target.value)}
                              placeholder={`Option ${optIndex + 1}`}
                              className="option-input"
                            />
                            <input
                              type="radio"
                              name={`correct-${index}`}
                              checked={question.correctAnswer === optIndex}
                              onChange={() => handleQuestionChange(index, 'correctAnswer', optIndex)}
                              className="correct-radio"
                              title="Mark as correct answer"
                            />
                            {question.options.length > 2 && (
                              <button
                                className="btn-remove-option"
                                onClick={() => handleRemoveOption(index, optIndex)}
                                title="Remove Option"
                              >
                                <FaTimes />
                              </button>
                            )}
                          </div>
                        ))}
                        <button
                          className="btn-add-option"
                          onClick={() => handleAddOption(index)}
                        >
                          <FaPlus /> Add Option
                        </button>
                      </div>
                    </>
                  )}

                  {question.type === 'true_false' && (
                    <div className="form-group">
                      <label>Correct Answer</label>
                      
                      {/* Preview integrated within form-group */}
                      {question.question && question.question.trim() && 
                       (question.correctAnswer === true || question.correctAnswer === false) && (
                        <div className="question-preview-integrated">
                          <div className="preview-question">
                            <strong>Question:</strong> {question.question}
                          </div>
                          <div className="preview-answer">
                            <strong>Correct Answer:</strong>{' '}
                            <span className="correct-answer-text">
                              {question.correctAnswer === true ? 'True' : 'False'}
                            </span>
                          </div>
                        </div>
                      )}
                      
                      <div className="true-false-options">
                        <label className={question.correctAnswer === true ? 'selected-answer' : ''}>
                          <input
                            type="radio"
                            name={`tf-${index}`}
                            checked={question.correctAnswer === true}
                            onChange={() => handleQuestionChange(index, 'correctAnswer', true)}
                          />
                          True
                        </label>
                        <label className={question.correctAnswer === false ? 'selected-answer' : ''}>
                          <input
                            type="radio"
                            name={`tf-${index}`}
                            checked={question.correctAnswer === false}
                            onChange={() => handleQuestionChange(index, 'correctAnswer', false)}
                          />
                          False
                        </label>
                      </div>
                    </div>
                  )}

                  {question.type === 'short_answer' && (
                    <div className="form-group">
                      <label>Reference Answer</label>
                      <textarea
                        value={question.referenceAnswer || question.correctAnswer || ''}
                        onChange={(e) => handleQuestionChange(index, 'referenceAnswer', e.target.value)}
                        rows="3"
                        placeholder="Enter the reference answer for grading..."
                      />
                      <small className="help-text">
                        This answer will be used as a reference when grading student responses.
                      </small>
                    </div>
                  )}

                  <div className="form-group">
                    <label>Explanation (Optional)</label>
                    <textarea
                      value={question.explanation || ''}
                      onChange={(e) => handleQuestionChange(index, 'explanation', e.target.value)}
                      rows="2"
                      placeholder="Enter explanation shown after answering..."
                    />
                  </div>
                </div>
              </div>
            ))
          )}

          <button className="btn-add-question" onClick={handleAddQuestion}>
            <FaPlus /> Add Question
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-quizzes-view">
      <div className="quizzes-header">
        <h2>
          <FaClipboardCheck /> Quiz Management
        </h2>
        <div className="search-box">
          <FaSearch />
          <input
            type="text"
            placeholder="Search quizzes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="quizzes-list">
        {filteredQuizzes.length === 0 ? (
          <div className="no-quizzes">No quizzes found</div>
        ) : (
          filteredQuizzes.map(quiz => (
            <div key={quiz.id} className="quiz-card">
              <div className="quiz-header">
                <div className="quiz-info">
                  <h3>{quiz.title}</h3>
                  <div className="quiz-meta">
                    <span className="lesson-badge">{quiz.lesson_title}</span>
                    <span className="course-badge">{quiz.course_title}</span>
                    <span className={`status-badge ${quiz.isActive ? 'active' : 'inactive'}`}>
                      {quiz.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
                <button
                  className="btn-edit"
                  onClick={() => handleEditQuestions(quiz)}
                >
                  <FaEdit /> Edit Questions
                </button>
              </div>

              <div className="quiz-details">
                <div className="detail-item">
                  <strong>Questions:</strong> {Array.isArray(quiz.questions) ? quiz.questions.length : 0}
                </div>
                <div className="detail-item">
                  <strong>Passing Score:</strong> {quiz.passingScore}%
                </div>
                <div className="detail-item">
                  <strong>XP Value:</strong> {quiz.xpValue}
                </div>
                {quiz.timeLimit && (
                  <div className="detail-item">
                    <strong>Time Limit:</strong> {quiz.timeLimit} minutes
                  </div>
                )}
              </div>

              {quiz.description && (
                <div className="quiz-description">
                  {quiz.description}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminQuizzesView;
